/**
 * IndexedDB-backed store for TAM list snapshots.
 *
 * We don't try to be a generic React-Query persister — the existing
 * localStorage `PersistQueryClientProvider` (in App.tsx) handles small
 * query results just fine. This store is purpose-built for one thing:
 * holding the per-list TAM snapshot blobs (5–30 MB each) that are too
 * large for localStorage.
 *
 * Usage:
 *   await snapshotStore.set(listId, signature, rows);
 *   const cached = await snapshotStore.get(listId);   // {signature, rows, lastUsedAt} | null
 *   await snapshotStore.evict(listId);
 *   await snapshotStore.clearAll();                   // call on sign-out / user-id change
 *
 * Quota / eviction:
 *   - Every `set` is wrapped in try/catch; on QuotaExceededError we run
 *     LRU eviction down to ~150 MB and retry once.
 *   - `last_used_at` is bumped on `get` so eviction stays accurate.
 *
 * Schema buster:
 *   - Each entry stores `__schema__: SCHEMA_VERSION`. On read, mismatched
 *     entries are dropped silently — same pattern as App.tsx's `buster`.
 *   - Bump SCHEMA_VERSION when CompanyView shape changes.
 */

import { createStore, get as idbGet, set as idbSet, del as idbDel, keys as idbKeys, clear as idbClear } from 'idb-keyval';
import type { CompanyView } from '@/hooks/useCompanies';

/** Bump when the cached row shape changes in a way that breaks the old cache.
 *  v2 = ICP gate applied at snapshot-build time. Pre-ICP entries (which
 *  contained unfiltered rows) auto-evict on first read because their
 *  __schema__ stamp is 'v1'. Also: per-client snapshots use scope-prefixed
 *  keys ('client-co:<id>', 'client-ct:<id>') instead of bare list-ids. */
const SCHEMA_VERSION = 'v2';

/** Soft cap on total cached bytes. LRU evicts down to ~75% of this. */
const SOFT_CAP_BYTES = 200 * 1024 * 1024; // 200 MB
const EVICT_TARGET_BYTES = 150 * 1024 * 1024;

/** Max number of cached lists. LRU evicts beyond this. */
const MAX_LISTS = 20;

interface SnapshotEntry<T = unknown> {
  __schema__: string;
  signature: string;
  rows: T[];
  built_at: number;
  last_used_at: number;
  byte_size_estimate: number;
}

const STORE = createStore('ob-crm-snapshots-db', 'snapshots');

function estimateBytes(rows: unknown[]): number {
  // Rough heuristic — JSON.stringify is too slow on 19k rows just for sizing.
  // Avg row ≈ 1.5 KB serialized.
  return rows.length * 1500;
}

async function totalBytes(): Promise<number> {
  const allKeys = await idbKeys(STORE);
  let total = 0;
  for (const k of allKeys) {
    const e = (await idbGet(k, STORE)) as SnapshotEntry | undefined;
    if (e?.byte_size_estimate) total += e.byte_size_estimate;
  }
  return total;
}

/** LRU eviction. Drops oldest-used entries until under EVICT_TARGET_BYTES
 *  AND under MAX_LISTS. */
async function evictLRU(): Promise<number> {
  const allKeys = await idbKeys(STORE);
  const entries: Array<{ key: IDBValidKey; e: SnapshotEntry }> = [];
  for (const k of allKeys) {
    const e = (await idbGet(k, STORE)) as SnapshotEntry | undefined;
    if (e) entries.push({ key: k, e });
  }
  // Oldest-used first
  entries.sort((a, b) => (a.e.last_used_at ?? 0) - (b.e.last_used_at ?? 0));

  let total = entries.reduce((acc, { e }) => acc + (e.byte_size_estimate ?? 0), 0);
  let count = entries.length;
  let evicted = 0;
  for (const { key, e } of entries) {
    if (total <= EVICT_TARGET_BYTES && count <= MAX_LISTS) break;
    await idbDel(key, STORE);
    total -= e.byte_size_estimate ?? 0;
    count -= 1;
    evicted += 1;
  }
  if (evicted > 0) {
    // eslint-disable-next-line no-console
    console.info(`[crm-snapshot] LRU evicted ${evicted} snapshot(s)`);
  }
  return evicted;
}

export const snapshotStore = {
  /** Read a snapshot. Returns null if missing or schema-mismatched. Bumps last_used_at.
   *  Generic over the row type — caller passes T (e.g. CompanyView, ContactWithCompany).
   *  scopeKey shape is up to the caller: bare list-id for per-list, or
   *  'client-co:<id>' / 'client-ct:<id>' for per-client. */
  async get<T = CompanyView>(scopeKey: string): Promise<{ signature: string; rows: T[]; lastUsedAt: number } | null> {
    try {
      const e = (await idbGet(scopeKey, STORE)) as SnapshotEntry<T> | undefined;
      if (!e) return null;
      if (e.__schema__ !== SCHEMA_VERSION) {
        // Auto-evict entries from a pre-ICP code version. They contained
        // unfiltered rows and would render the wrong count.
        // eslint-disable-next-line no-console
        console.info(`[crm-snapshot] evicting stale-schema entry ${scopeKey} (was '${e.__schema__}', want '${SCHEMA_VERSION}')`);
        await idbDel(scopeKey, STORE);
        return null;
      }
      // Bump last_used_at (fire-and-forget so reads stay fast)
      idbSet(scopeKey, { ...e, last_used_at: Date.now() }, STORE).catch(() => {});
      return { signature: e.signature, rows: e.rows, lastUsedAt: e.last_used_at };
    } catch (err) {
      console.error('[crm-snapshot] get failed', { scopeKey, err });
      return null;
    }
  },

  /** Atomic-ish write: only stores after `rows` is fully assembled by the caller. */
  async set<T = CompanyView>(scopeKey: string, signature: string, rows: T[]): Promise<void> {
    const entry: SnapshotEntry<T> = {
      __schema__: SCHEMA_VERSION,
      signature,
      rows,
      built_at: Date.now(),
      last_used_at: Date.now(),
      byte_size_estimate: estimateBytes(rows),
    };
    try {
      await idbSet(scopeKey, entry, STORE);
    } catch (err: any) {
      const isQuota = err?.name === 'QuotaExceededError' || /quota/i.test(err?.message ?? '');
      if (!isQuota) {
        console.error('[crm-snapshot] set failed', { scopeKey, err });
        return;
      }
      console.warn('[crm-snapshot] quota exceeded, evicting LRU and retrying', { scopeKey });
      await evictLRU();
      try {
        await idbSet(scopeKey, entry, STORE);
      } catch (err2) {
        console.error('[crm-snapshot] set failed after eviction', { scopeKey, err: err2 });
      }
    }
    // Best-effort soft-cap eviction post-write (fire-and-forget)
    totalBytes()
      .then(async (t) => {
        const allKeys = await idbKeys(STORE);
        if (t > SOFT_CAP_BYTES || allKeys.length > MAX_LISTS) {
          await evictLRU();
        }
      })
      .catch(() => {});
  },

  /** Drop one entry. */
  async evict(scopeKey: string): Promise<void> {
    try {
      await idbDel(scopeKey, STORE);
    } catch (err) {
      console.error('[crm-snapshot] evict failed', { scopeKey, err });
    }
  },

  /** CRITICAL: call on sign-out and user-id change. */
  async clearAll(): Promise<void> {
    try {
      await idbClear(STORE);
    } catch (err) {
      console.error('[crm-snapshot] clearAll failed', { err });
    }
  },

  /** Diagnostics for the ?debug=snapshot overlay. */
  async stats(): Promise<{ count: number; totalBytes: number }> {
    const allKeys = await idbKeys(STORE);
    return { count: allKeys.length, totalBytes: await totalBytes() };
  },
};
